import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { WorkGroupEmptyState } from '@/features/workgroups/components/work-group-empty-state';
import { WorkGroupTrashList } from '@/features/workgroups/components/work-group-trash-list';
import { PrivateAccessReasonDialog } from '@/features/workgroups/components/private-access-reason-dialog';
import { usePermissions } from '@/hooks/use-permissions';
import {
  useEmptyWorkGroupTrash,
  useWorkGroup,
  useWorkGroupTrash,
  useWorkgroupsPrivateAccess,
  workgroupsMutationErrorMessage,
} from '@/hooks/use-workgroups';
import {
  useWorkgroupsUIStore,
  WORKGROUPS_PRIVATE_REASON_MIN_LEN,
} from '@/stores/workgroups-ui-store';
import { toast } from '@/components/ui/toaster';

export function WorkGroupsTrashPage() {
  const { workGroupId } = useParams<{ workGroupId: string }>();
  const { hasCapability } = usePermissions();
  const canViewTrash = hasCapability('workgroups.trash.view');
  const canEmptyTrash =
    hasCapability('workgroups.trash.empty') && hasCapability('workgroups.admin.all');

  const privateAccessReason = useWorkgroupsUIStore((s) => {
    if (!workGroupId) return undefined;
    const trimmed = s.privateAccessByWorkGroupId[workGroupId]?.trim() ?? '';
    return trimmed.length >= WORKGROUPS_PRIVATE_REASON_MIN_LEN ? trimmed : undefined;
  });

  const { data: wg, error: wgError } = useWorkGroup(workGroupId, privateAccessReason);
  const access = useWorkgroupsPrivateAccess(workGroupId, wg);

  const forbiddenNeedsReason =
    axios.isAxiosError(wgError) &&
    wgError.response?.status === 403 &&
    access.canPrivateAdmin &&
    !privateAccessReason;

  const blockContent = access.reasonMissing || forbiddenNeedsReason;

  const { data: trash, isLoading, error, refetch } = useWorkGroupTrash(
    workGroupId,
    undefined,
    canViewTrash && !blockContent,
    privateAccessReason,
  );
  const emptyMutation = useEmptyWorkGroupTrash();

  if (!workGroupId) {
    return <p className="text-sm text-muted-foreground">Missing work group.</p>;
  }

  if (!canViewTrash) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <p className="text-sm text-muted-foreground">You do not have permission to view WorkGroups trash.</p>
        <Button variant="outline" size="sm" asChild>
          <Link to={`/workgroups/${workGroupId}`}>Back to work group</Link>
        </Button>
      </div>
    );
  }

  const hasEntries = (trash?.folders.length ?? 0) + (trash?.files.length ?? 0) > 0;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PrivateAccessReasonDialog
        open={access.promptOpen || forbiddenNeedsReason}
        onSubmit={access.submitReason}
      />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Trash</h1>
          <p className="text-sm text-muted-foreground">{wg?.name?.trim() || 'Work group'}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/workgroups/${workGroupId}`}>Back to browse</Link>
          </Button>
          {access.needsReason && privateAccessReason ? (
            <Button variant="ghost" size="sm" onClick={() => access.clearReason()}>
              Change admin reason
            </Button>
          ) : null}
          {canEmptyTrash ? (
            <Button
              variant="destructive"
              size="sm"
              disabled={!hasEntries || emptyMutation.isPending || blockContent}
              onClick={() => {
                if (
                  window.confirm(
                    'Permanently delete all files in trash for this work group? Trashed items still count toward storage until removed. This cannot be undone.',
                  )
                ) {
                  emptyMutation.mutate(
                    { workGroupId, privateAccessReason },
                    {
                      onSuccess: () => void refetch(),
                      onError: (err) => {
                        toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
                      },
                    },
                  );
                }
              }}
            >
              Empty trash (files)
            </Button>
          ) : null}
        </div>
      </div>

      {forbiddenNeedsReason ? (
        <p className="text-sm text-muted-foreground">
          Administrator access to this private work group requires a reason.
        </p>
      ) : null}

      {!blockContent ? (
        <>
          <div
            className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
            data-testid="wg-trash-quota-banner"
          >
            Items in trash still count toward your storage quota until they are permanently deleted.
            Empty trash or permanently delete individual items to free space.
          </div>

          {isLoading ? <p className="text-sm text-muted-foreground">Loading trash…</p> : null}
          {error ? <p className="text-sm text-destructive">Could not load trash.</p> : null}

          {!isLoading && !error && trash && !hasEntries ? (
            <WorkGroupEmptyState
              title="Trash is empty"
              description="Deleted files and folders will appear here."
            />
          ) : null}

          {trash && hasEntries ? (
            <WorkGroupTrashList
              workGroupId={workGroupId}
              folders={trash.folders}
              files={trash.files}
              privateAccessReason={privateAccessReason}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
